import { ResponseForConvertOfferAfx2jsonType } from '@afidium/ts-facade';
import { Injectable } from '@angular/core';
import { IWenji } from 'libraries/interfaces/IWenji';
import { AirQuoteResourceService } from 'libraries/service/resources/airQuote/airQuoteResource.service';
import { IAirGetQuotedAvailabilityRequest } from 'libraries/service/resources/model/booking/air/IAirQuotedAvailability';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { IAirCreateBookingRequest } from "libraries/service/resources/model/booking/air/IAirCreateBooking";

declare var wenji: IWenji;

export interface ICacheQuotedAvailabilityKeys {
    bookingKey: string;
}

@Injectable()
export class CacheQuotedAvailabilityService {

    private readonly cache: Map<string, Observable<ResponseForConvertOfferAfx2jsonType>> = new Map();
    private readonly withServiceCache: Map<string, Observable<ResponseForConvertOfferAfx2jsonType>> = new Map();
    private readonly requestCache: Map<string, IAirGetQuotedAvailabilityRequest> = new Map();
    private readonly requestCacheBeforeCreateBooking: Map<string, IAirGetQuotedAvailabilityRequest> = new Map();
    private readonly selectedServiceCache: Map<string, any> = new Map();
    private readonly createBookingWithServiceSelectionRequestCache: Map<string, IAirCreateBookingRequest> = new Map();

    constructor(
        private readonly airQuoteResource: AirQuoteResourceService,
    ) {
    }

    public getRequest(key: string): IAirGetQuotedAvailabilityRequest {
        return this.requestCache.get(key);
    }

    public getCacheQuotedAvailability(request: IAirGetQuotedAvailabilityRequest, keys: ICacheQuotedAvailabilityKeys)
        : Observable<ResponseForConvertOfferAfx2jsonType> {
        const key: string = keys.bookingKey;

        if (!this.cache.has(key)) {
            const response: Observable<ResponseForConvertOfferAfx2jsonType> =
                this.getQuotedAvailability(request, key)
                    .pipe(
                        shareReplay(1),
                        catchError((error: Error): Observable<never> => {
                            this.cache.delete(key);
                            return throwError(error);
                        }),
                    );

            this.cache.set(key, response);
            return response;
        }
        return this.cache.get(key);
    }


    public getCacheQuotedAvailabilityWithService(request: IAirGetQuotedAvailabilityRequest, keys: ICacheQuotedAvailabilityKeys, storeRequestBeforeCreateBooking?: boolean)
        : Observable<ResponseForConvertOfferAfx2jsonType> {
        const key: string = keys.bookingKey;
        if(storeRequestBeforeCreateBooking){
            this.requestCacheBeforeCreateBooking.set(key, request);
        }

        if (!this.withServiceCache.has(key)) {
            const response: Observable<ResponseForConvertOfferAfx2jsonType> =
                this.getQuotedAvailabilityWithService(request, key)
                    .pipe(
                        shareReplay(1),
                        catchError((error: Error): Observable<never> => {
                            this.withServiceCache.delete(key);
                            return throwError(error);
                        }),
                    );
            // certainly useless, I'll remove once I'm sure
            // this.requestCache.set(key, request);

            this.withServiceCache.set(key, response);
            return response;
        }
        return this.withServiceCache.get(key);
    }

    public storeSelectionForQuote(key: string, data: any): void {
        this.selectedServiceCache.set(key, data);
    }
    public storeCreateBookingWithServiceSelectionRequest(data: any): void {
        this.createBookingWithServiceSelectionRequestCache.set('request', data);
    }

    public getBookingWithServiceSelectionRequest(): IAirCreateBookingRequest {
        return this.createBookingWithServiceSelectionRequestCache.get('request');
    }
    public hasBookingWithServiceSelectionRequest(): boolean {
        return this.createBookingWithServiceSelectionRequestCache.has('request');
    }
    public clearCache(): void {
        this.cache.clear();
        this.clearServiceCache();
        this.clearRequestCache();
    }
    public clearServiceCache(): void {
        this.withServiceCache.clear();
        this.selectedServiceCache.clear();
        this.createBookingWithServiceSelectionRequestCache.clear();
    }

    public clearRequestCache(): void {
        this.requestCache.clear();
    }

    public updateRequest(bookingKey: string, request: IAirGetQuotedAvailabilityRequest): void {
        this.requestCache.set(bookingKey, request);
    }

    private getQuotedAvailability(request: IAirGetQuotedAvailabilityRequest, key?: string):
        Observable<ResponseForConvertOfferAfx2jsonType> {

        const target: string = wenji.ui.target;

        return this.airQuoteResource
            .postGetQuotedAvailability(request, target)
            .pipe(
                tap((response: ResponseForConvertOfferAfx2jsonType): void => {
                    if(key){
                        //cache request for .../services and validation RQ , launched in widget
                        //we need to replace the transactionId by the one given in quoteOfferRS
                        // this.requestCache.set(key, {...request, transactionId: response.transaction.transactionId});
                        this.requestCache.set(key, request);
                    }
                }),
                map((response: ResponseForConvertOfferAfx2jsonType): ResponseForConvertOfferAfx2jsonType => response)
            );
    }

    private getQuotedAvailabilityWithService(request: IAirGetQuotedAvailabilityRequest, key?: string):
        Observable<ResponseForConvertOfferAfx2jsonType> {
        const target: string = wenji.ui.target;

        return this.airQuoteResource
            .postGetQuotedAvailabilityWithService(request, target);
    }
}
